/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Tabs, Tab, Chip, IconButton, useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import { useSnackbar } from 'notistack';
import { useCloseOnBack } from '../../hooks/useCloseOnBack';
import * as eqpApi from '../../lib/api/eqp';
import EqpInfoTab from './EqpInfoTab';
import EqpLogTab from './EqpLogTab';
import EqpPlanTab from './EqpPlanTab';
import EqpProgramTab from './EqpProgramTab';
import EqpToolDiagTab from './EqpToolDiagTab';
import EqpMagazineTab from './EqpMagazineTab';
import EqpToolpathTab from './EqpToolpathTab';
import EqpDayDataView from './EqpDayDataView';
import { TAB_KEYS, getTabKeysForMode, resolveInitialTabIndex, canEditInMode } from './eqpDetailTabs';

const TAB_LABELS = {
  [TAB_KEYS.INFO]: '기본정보',
  [TAB_KEYS.PLAN]: '예상 가동시간',
  [TAB_KEYS.PROGRAM]: '가공 프로그램',
  [TAB_KEYS.TOOL_DIAG]: '공구 진단',
  [TAB_KEYS.MAGAZINE]: '매거진 현황',
  [TAB_KEYS.TOOLPATH]: '가공 진행',
  [TAB_KEYS.DAY_DATA]: '수집 데이터',
};

/**
 * 장비 상세창 - 장비관리(mode="manage", 기본값)와 생산 현황/모니터링 보드(mode="view")가
 * 같은 창을 연다(2026-10-19 통합). 탭 집합은 모드에 따라 다르고(eqpDetailTabs.js 참고),
 * view 모드는 설정류(기본정보/통신 설정/공구 진단)를 전부 뺀 "보기 전용"이다.
 *
 * canManage: 장비관리 메뉴 쓰기 권한(useMenuAccess 등으로 호출 쪽이 넘겨준다) - manage 모드에서도
 * 이 값이 false면 저장/삭제/기준 설정 같은 편집 컨트롤을 가린다. 자세한 한계는 useMenuAccess.js 참고.
 */
const EqpDetailModal = ({
  open, eqpData, onClose, onSave, onDelete,
  eqpTypeOptions = [], eqpStatusOptions = [], agentOptions = [],
  logTypeLabels = {}, logTypeColors = {},
  mode = 'manage', canManage = true, initialTab,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isNew = !eqpData;
  const [tabIndex, setTabIndex] = useState(0);
  const [logList, setLogList]   = useState([]);
  const [logTick, setLogTick]   = useState(0);

  const tabKeys = useMemo(() => getTabKeysForMode(mode, { isNew }), [mode, isNew]);
  const activeKey = tabKeys[tabIndex];
  const canEdit = canEditInMode(mode, canManage);
  const isViewMode = mode === 'view';

  useCloseOnBack(open, onClose);

  useEffect(() => {
    if (open) setTabIndex(resolveInitialTabIndex(tabKeys, initialTab));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, isNew, initialTab]);

  useEffect(() => {
    if (!open || isNew || !eqpData?.eqpUuid || !tabKeys.includes(TAB_KEYS.LOG)) return;
    eqpApi.getLogList({ eqpUuid: eqpData.eqpUuid })
      .then(res => setLogList(res.data.list || []))
      .catch(() => enqueueSnackbar('운영이력 조회 실패', { variant: 'error' }));
  }, [open, eqpData?.eqpUuid, logTick, tabKeys]);

  const handleLogSave = async (values, selectedLog) => {
    try {
      const payload = { ...values, eqpUuid: eqpData.eqpUuid };
      if (selectedLog) {
        await eqpApi.editLog(selectedLog.logUuid, payload);
        enqueueSnackbar('운영이력을 수정했습니다.', { variant: 'success' });
      } else {
        await eqpApi.addLog(payload);
        enqueueSnackbar('운영이력을 등록했습니다.', { variant: 'success' });
      }
      setLogTick(t => t + 1);
    } catch (e) {
      enqueueSnackbar('저장에 실패했습니다.', { variant: 'error' });
    }
  };

  const handleLogDelete = async (logUuid) => {
    try {
      await eqpApi.removeLog(logUuid);
      enqueueSnackbar('운영이력을 삭제했습니다.', { variant: 'success' });
      setLogTick(t => t + 1);
    } catch (e) {
      enqueueSnackbar('삭제에 실패했습니다.', { variant: 'error' });
    }
  };

  const tabLabel = (key) => (key === TAB_KEYS.LOG ? `운영이력 (${logList.length})` : TAB_LABELS[key]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0, flexWrap: 'wrap' }}>
          {isNew ? '장비 등록' : (
            <>
              {eqpData?.eqpNm}
              <Chip label={eqpData?.eqpCd} size="small" variant="outlined" />
              {eqpData?.eqpTyp && (
                <Chip
                  label={Object.fromEntries(eqpTypeOptions.map(o => [o.slcCd, o.itemNm]))[eqpData.eqpTyp] || eqpData.eqpTyp}
                  size="small"
                  variant="outlined"
                  color="primary"
                />
              )}
              {isViewMode && (
                <Chip label="조회 전용" size="small" variant="outlined" color="default" />
              )}
            </>
          )}
        </Box>
        <IconButton aria-label="닫기" onClick={onClose} size="small" sx={{ flexShrink: 0 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
        <Tabs
          value={tabIndex}
          onChange={(_, v) => setTabIndex(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          {tabKeys.map((key) => (
            <Tab key={key} label={tabLabel(key)} />
          ))}
        </Tabs>
      </Box>

      <DialogContent sx={{ pt: 2, minHeight: 420 }}>
        {activeKey === TAB_KEYS.INFO && (
          <EqpInfoTab
            eqpData={eqpData}
            onSave={onSave}
            onClose={onClose}
            onDelete={onDelete}
            eqpTypeOptions={eqpTypeOptions}
            eqpStatusOptions={eqpStatusOptions}
            agentOptions={agentOptions}
            canEdit={canEdit}
          />
        )}
        {activeKey === TAB_KEYS.LOG && (
          <EqpLogTab
            eqpUuid={eqpData?.eqpUuid}
            list={logList}
            onSave={handleLogSave}
            onDelete={handleLogDelete}
            logTypeLabels={logTypeLabels}
            logTypeColors={logTypeColors}
          />
        )}
        {activeKey === TAB_KEYS.PLAN && (
          <EqpPlanTab eqpUuid={eqpData?.eqpUuid} />
        )}
        {activeKey === TAB_KEYS.PROGRAM && (
          <EqpProgramTab eqpUuid={eqpData?.eqpUuid} />
        )}
        {activeKey === TAB_KEYS.TOOL_DIAG && (
          <EqpToolDiagTab eqpUuid={eqpData?.eqpUuid} />
        )}
        {activeKey === TAB_KEYS.MAGAZINE && (
          <EqpMagazineTab eqpUuid={eqpData?.eqpUuid} canEdit={canEdit} />
        )}
        {activeKey === TAB_KEYS.TOOLPATH && (
          <EqpToolpathTab eqpUuid={eqpData?.eqpUuid} />
        )}
        {activeKey === TAB_KEYS.DAY_DATA && (
          <EqpDayDataView eqpUuid={eqpData?.eqpUuid} machineLabel={eqpData?.eqpNm} />
        )}
      </DialogContent>

      {activeKey !== TAB_KEYS.INFO && (
        <DialogActions>
          <Button onClick={onClose}>닫기</Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default EqpDetailModal;
